import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { getAssetUrl } from '../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import FeaturedBadgeSelector from '../components/FeaturedBadgeSelector';
import FeaturedBadges from '../components/FeaturedBadges';
import ShareButton from '../components/ShareButton';
import { Star } from 'lucide-react';

const UserProfile = () => {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [featuredBadges, setFeaturedBadges] = useState([]);
  const [showBadgeSelector, setShowBadgeSelector] = useState(false);

  const isOwnProfile = currentUser && currentUser.id === userId;

  const fetchProfile = useCallback(async () => {
    try {
      const response = await api.get(`/users/${userId}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchUserBadges = useCallback(async () => {
    try {
      // Fetch earned badges from new badge system for the specific user
      const [badgesRes, featuredRes] = await Promise.all([
        api.get(`/users/${userId}/badges`),
        api.get(`/users/${userId}/featured-badges`)
      ]);
      
      setEarnedBadges(badgesRes.data.badges || []);
      setFeaturedBadges(featuredRes.data.featured_badges || []);
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
    fetchUserBadges();
  }, [fetchProfile, fetchUserBadges]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mx-auto"></div>
          <p className="text-gray-500 mt-4 font-mono text-sm">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black text-white mb-4">404</h1>
          <p className="text-gray-400 mb-6">User not found</p>
          <Link to="/" className="text-green-500 hover:text-green-400 font-mono uppercase">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  const joinedDate = new Date(profile.joined).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long'
  });

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="text-green-500 hover:text-green-400 font-mono text-sm uppercase tracking-wider">
              ← Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Profile Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Profile Header */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 mb-8">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            {profile.profile_image_url ? (
              <img 
                src={getAssetUrl(profile.profile_image_url)}
                alt={profile.name}
                className="w-24 h-24 rounded-full object-cover border-2 border-green-500"
              />
            ) : (
              <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center text-white text-4xl font-black">
                {profile.name.charAt(0).toUpperCase()}
              </div>
            )}
            
            {/* User Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-3xl font-black text-white tracking-tight">
                  {profile.name}
                </h1>
                {/* Featured Badges Display */}
                {featuredBadges && featuredBadges.length > 0 && (
                  <FeaturedBadges badges={featuredBadges} size="md" />
                )}
              </div>
              
              <p className="text-gray-400 font-mono text-sm mb-4">
                @{profile.username}
              </p>

              {profile.role === 'admin' && (
                <span className="inline-block px-3 py-1 bg-red-900/30 border border-red-800 rounded text-red-500 text-xs font-mono uppercase mr-2" data-testid="profile-admin-badge">
                  Admin
                </span>
              )}

              {profile.role === 'community_leader' && (
                <span className="inline-block px-3 py-1 bg-blue-900/30 border border-blue-800 rounded text-blue-300 text-xs font-mono uppercase mr-2" data-testid="profile-moderator-badge">
                  Community Moderator
                </span>
              )}

              {profile.ward && (
                <span className="inline-block px-3 py-1 bg-gray-800 border border-gray-700 rounded text-gray-400 text-xs font-mono uppercase">
                  {profile.ward}
                </span>
              )}

              <p className="text-gray-500 text-sm mt-4 font-mono">
                Member since {joinedDate}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 text-center">
            <div className="text-4xl font-black text-green-500 mb-2">
              {profile.stats.posts}
            </div>
            <div className="text-gray-400 text-sm font-mono uppercase tracking-wider">
              Posts Submitted
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 text-center">
            <div className="text-4xl font-black text-green-500 mb-2">
              {profile.stats.messages}
            </div>
            <div className="text-gray-400 text-sm font-mono uppercase tracking-wider">
              Messages Sent
            </div>
          </div>
        </div>

        {/* Badges Section */}
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-black text-white tracking-tight">
              Badges Earned
            </h2>
            <div className="flex gap-3">
              {isOwnProfile && earnedBadges.length > 0 && (
                <button
                  onClick={() => setShowBadgeSelector(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-bold text-sm rounded uppercase tracking-wider transition-colors"
                >
                  <Star className="w-4 h-4" />
                  Select Featured
                </button>
              )}
              <Link 
                to="/badges/legend" 
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-bold text-sm rounded uppercase tracking-wider transition-colors"
              >
                Badge Legend
              </Link>
            </div>
          </div>

          {/* Featured Badges Showcase */}
          {isOwnProfile && featuredBadges.length > 0 && (
            <div className="bg-gradient-to-r from-[#C5A52A]/10 to-transparent border border-[#C5A52A]/30 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-[#C5A52A]" />
                <h3 className="font-mono text-sm text-white uppercase tracking-wider">Your Featured Badges</h3>
              </div>
              <p className="font-mono text-xs text-gray-400 mb-3">
                These badges appear next to your name throughout the site
              </p>
              <div className="flex gap-4">
                {featuredBadges.map((badge, idx) => {
                  const badgeUrl = badge.badge_url || badge.icon_url;
                  return (
                    <div key={idx} className="text-center">
                      {badgeUrl && badgeUrl.startsWith('http') ? (
                        <img
                          src={badgeUrl}
                          alt={badge.badge_name || 'Featured Badge'}
                          className="w-12 h-12 object-contain mx-auto mb-2"
                        />
                      ) : (
                        <div className="text-4xl mb-2">{badgeUrl || '🏅'}</div>
                      )}
                      <p className="font-mono text-xs text-gray-500 max-w-[80px] truncate">
                        {badge.badge_name}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {earnedBadges && earnedBadges.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {earnedBadges.map((badge) => (
                <div
                  key={badge.id}
                  className="bg-gray-950 border border-gray-800 rounded-lg p-6 hover:border-green-700 transition-all"
                >
                  <div className="flex items-start gap-4">
                    {badge.badge_icon_url && badge.badge_icon_url.startsWith('http') ? (
                      <img 
                        src={badge.badge_icon_url} 
                        alt={badge.badge_name}
                        className="w-12 h-12 object-contain"
                      />
                    ) : (
                      <div className="text-5xl">{badge.badge_icon_url || '🏅'}</div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-lg mb-1">
                        {badge.badge_name}
                      </h3>
                      <p className="text-gray-400 text-sm mb-2">
                        {badge.badge_description}
                      </p>
                      {badge.earned_at && (
                        <p className="text-gray-500 text-xs font-mono">
                          Earned: {new Date(badge.earned_at).toLocaleDateString()}
                        </p>
                      )}
                      {badge.earned_at_venue && (
                        <p className="text-green-500 text-xs font-mono mt-1">
                          {badge.earned_at_venue}
                        </p>
                      )}
                      <div className="mt-3">
                        <ShareButton
                          url={`/profile/${userId}`}
                          text={`I just earned the "${badge.badge_name}" badge on CYQR.CA — the civic accountability platform for Regina residents!`}
                          label="SHARE BADGE"
                          size="xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏅</div>
              <p className="text-gray-400 font-mono text-sm">
                No badges earned yet. Start checking in at locations to earn badges!
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Featured Badge Selector Modal */}
      {showBadgeSelector && (
        <FeaturedBadgeSelector
          onClose={() => setShowBadgeSelector(false)}
          onSave={() => {
            fetchUserBadges();
            setShowBadgeSelector(false);
          }}
        />
      )}
    </div>
  );
};

export default UserProfile;
